import { cn } from "@peerigon/pupper/tailwind";
import { useFormatter, useLocale, useTranslations } from "next-intl";
import React, { useMemo } from "react";
import { Hx } from "uberschrift";
import {
	SiteLocale,
	TeaseredBlogPostItemFragment,
} from "src/__generated__/graphql";

import { stringListToArray } from "src/hygraph-to-dato";
import { getBlogPath } from "src/lib/paths";
import { GoToBlogArticleWrapper } from "./GoToBlogArticleWrapper";
import { ImageContainer } from "./ImageContainer";
import { TinyHeadline } from "../../styles/FontFaces";
import { colors } from "../../styles/variables";
import { buttonTextClassName } from "../Button/Button";
import { CmsImage } from "../Image/CmsImage";
import { readCmsImageData } from "../Image/utils";
import Link from "../Link/Link";
import Tags from "../Tags/Tags";
import TextAndAnimatedArrow from "../TextAndAnimatedArrow/TextAndAnimatedArrow";
import type { CSSProperties } from "react";

type BlogPostTileProps = TeaseredBlogPostItemFragment & {
	isFullTeaser?: boolean;
	isTeaserSection?: boolean | undefined;
};

const colorSchemes = [
	{
		color: "black",
		backgroundColor: colors.white,
		secondaryColor: colors.gray.dark,
	},
	{
		color: "black",
		backgroundColor: colors.mint,
		secondaryColor: colors.nightSky,
	},
	{
		color: "white",
		backgroundColor: colors.blueberry,
		secondaryColor: colors.moonRaker,
	},
	{
		color: "black",
		backgroundColor: colors.ice,
		secondaryColor: colors.gray.dark,
	},
	{
		color: "white",
		backgroundColor: colors.nightSky,
		secondaryColor: colors.moonRaker,
	},
];

const cheapHash = (input: string) =>
	input
		.split("")
		.map((char) => char.charCodeAt(0))
		.reduce((sum, value) => sum + value, input.length) * input.length;

const BlogPostTile: React.FC<BlogPostTileProps> = ({
	routeName,
	slug,
	headerImage,
	publishingDate,
	title,
	tags,
	authors,
	isFullTeaser,
	isTeaserSection = false,
}) => {
	const locale = useLocale() as SiteLocale;
	const format = useFormatter();
	const routeNameHash = useMemo(() => cheapHash(routeName), [routeName]);
	const colorScheme = colorSchemes[routeNameHash % colorSchemes.length];

	const t = useTranslations();
	const date = format.dateTime(new Date(publishingDate), {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});

	// if the blog post is not translated we link to the locale that is passed as default
	const localizedBlogRoute = getBlogPath(locale, routeName);

	const headerImageData = readCmsImageData(headerImage);

	if (!colorScheme) {
		return null;
	}

	return (
		<Link
			to={localizedBlogRoute}
			key={slug}
			className={cn(
				"group relative no-underline shadow-(--shadow-light)",
				/* the outlines of white tiles should be distinguishable from the background on hover */
				"hover:shadow-(--shadow-light)",
				(isFullTeaser || isTeaserSection) && "row-span-3",
				"md:flex md:h-full",
				isFullTeaser
					? "md:col-[1/3] md:row-span-3 md:flex-row"
					: "md:row-span-2 md:flex-col",
				headerImageData ? "lg:row-span-3" : "lg:row-span-2",
				isFullTeaser
					? "lg:zoom-hover-effect-1.09 lg:col-[1/4]"
					: "lg:zoom-hover-effect-1.05",
				"lg:zoom-element-hover-effect",
			)}
		>
			{headerImageData && (
				<ImageContainer
					fullTeaser={isFullTeaser}
					isTeaserSection={isTeaserSection}
					className={
						isFullTeaser ? "full-height-image" : "full-height-lg"
					}
				>
					<CmsImage
						image={headerImageData}
						style={{ height: "100%" }}
						className={cn(
							isFullTeaser || isTeaserSection
								? "h-full w-full object-cover"
								: "w-(--masonry-mobile-image)",
							isFullTeaser
								? "md:h-full md:w-full"
								: "md:w-(--masonry-mobile-image)",
							"lg:relative lg:w-full lg:object-cover lg:[transition:transform_0.4s_ease]",
							isFullTeaser
								? "lg:h-full"
								: "lg:h-(--masonry-image-tile)",
						)}
					/>
				</ImageContainer>
			)}
			{/* Content card */}
			<div
				className={cn(
					"flex w-full flex-col justify-between bg-(--tile-bg)",
					isFullTeaser || isTeaserSection ? "h-auto" : "h-full",
					isFullTeaser ? "md:h-auto" : "md:h-full",
					headerImageData
						? "lg:min-h-(--masonry-image-tile)"
						: "lg:min-h-(--masonry-content-min-height)",
				)}
				style={
					{
						"--tile-bg": colorScheme.backgroundColor,
					} as CSSProperties
				}
			>
				{/* Text area */}
				<div
					className={cn(
						headerImageData && !isFullTeaser && !isTeaserSection
							? "w-[65%]"
							: "w-auto",
						headerImageData && !isFullTeaser
							? "md:w-[65%]"
							: "md:w-auto",
						"lg:h-full lg:w-auto",
						"lg:m-px-30 m-[15px]",
					)}
				>
					<TinyHeadline
						color={colorScheme.secondaryColor}
						className="w-[80%] leading-[0.9] lg:w-full"
					>
						{authors.map((author) => `${author.name}, `)}
						{date}
					</TinyHeadline>
					<Hx
						className={cn(
							"font-adelphi mt-px-5 text-px-24 leading-regular-1 m-0 font-bold",
							isFullTeaser ? "md:text-px-44" : "md:text-px-32",
							!isFullTeaser && [
								"[display:-webkit-box]",
								"[-webkit-line-clamp:3]",
								"[-webkit-box-orient:vertical]",
								"overflow-hidden",
								"wrap-anywhere",
								"text-balance",
							],
						)}
						style={{ color: colorScheme.color }}
						dangerouslySetInnerHTML={{ __html: title }}
					/>
					<Tags
						tags={stringListToArray(tags)}
						color={colorScheme.secondaryColor}
					/>
				</div>
				<GoToBlogArticleWrapper color={colorScheme.color}>
					<TextAndAnimatedArrow
						text={t("goToBlogArticle")}
						buttonTextClassName={buttonTextClassName}
					/>
				</GoToBlogArticleWrapper>
			</div>
		</Link>
	);
};

export default BlogPostTile;
